/**
 * Slug + share-link helpers. Keep this logic identical to the backend's
 * `_slugify()` in server.py so both sides match.
 */
export function slugify(text) {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/**
 * Build the public share URL for a vehicle (portfolio or product).
 * Sends users through the backend's OG endpoint so Discord/Twitter/Slack
 * render a rich preview with the asset's own thumbnail.
 *
 * `kind` optionally biases the backend's disambiguation when the same slug
 * exists on BOTH a portfolio entry and a shop product. Pass "portfolio" or
 * "product" from the side that owns the URL. If omitted, backend defaults
 * to product-first (legacy behavior).
 */
export function buildShareUrl(slug, kind) {
  if (!slug) return "";
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const qs = kind ? `?k=${encodeURIComponent(kind)}` : "";
  return `${base.replace(/\/+$/, "")}/api/share/${encodeURIComponent(slug)}${qs}`;
}

/**
 * Compute the URL bar form that should be shown while a vehicle is open.
 * We deliberately use the SAME `/api/share/<slug>` path as the public share
 * URL so that whatever the user copies from their URL bar will render a
 * rich Discord/Twitter preview. (Refreshing this URL hits the backend,
 * which JS-redirects humans back to `/?v=<slug>#<kind>` — handled by SPA.)
 *
 * `kind` prevents refresh from bouncing the visitor to the wrong page when
 * the slug exists in both sections (e.g. a portfolio entry AND a product
 * share the same name — refreshing on portfolio must NOT open the shop).
 */
export function getAddressBarShareUrl(slug, kind) {
  if (!slug) return "/";
  const qs = kind ? `?k=${encodeURIComponent(kind)}` : "";
  return `/api/share/${encodeURIComponent(slug)}${qs}`;
}

/**
 * Best-effort clipboard write with a fallback for older browsers.
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  // Legacy fallback
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}
